import React from "react";
import { DatabaseZap, Lock } from "lucide-react";
import { useComposerBrainBankSync } from "../../hooks/useComposerBrainBankSync";

/**
 * Shown in a composer once files are attached, so the user sees before Send
 * whether their attachments also go to the conversation's BrainBank -- and can
 * turn that off for the rest of the chat. Disclosure is inline rather than a
 * dialog, and nothing asks per file.
 */
const BrainBankSyncStrip: React.FC<{ hasAttachments: boolean }> = ({ hasAttachments }) => {
  const { mode, brainBank, saveToBrainBank, setSaveToBrainBank } = useComposerBrainBankSync();

  if (!hasAttachments || mode === "none") return null;

  if (mode === "no_permission") {
    return (
      <div className="w-full flex items-start gap-2 rounded-lg bg-surface-2 px-3 py-2 my-2 text-xs text-muted">
        <Lock size={14} strokeWidth={2} className="mt-0.5 shrink-0" />
        <span>
          This chat uses a BrainBank you can't add files to. Your attachments stay in this chat.
        </span>
      </div>
    );
  }

  const name = brainBank?.name || "this BrainBank";

  return (
    <div className="w-full flex items-center gap-3 rounded-lg bg-surface-brand px-3 py-2 my-2 text-xs text-primary">
      <DatabaseZap size={14} strokeWidth={1.75} className="shrink-0 text-violet-600" />
      <span className="flex-grow">
        {saveToBrainBank ? (
          <>
            Attachments are also saved to <strong>{name}</strong>, where everyone with access can
            read them.
          </>
        ) : (
          <>
            Attachments stay in this chat. Turn on to also save them to <strong>{name}</strong>.
          </>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={saveToBrainBank}
        onClick={() => setSaveToBrainBank(!saveToBrainBank)}
        className="flex items-center gap-2 shrink-0 text-muted hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded-md"
      >
        <span
          className={`relative inline-block h-5 w-9 rounded-full transition-colors ${
            saveToBrainBank ? "bg-violet-600" : "bg-surface-3"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
              saveToBrainBank ? "left-[18px]" : "left-0.5"
            }`}
          />
        </span>
        Save to BrainBank
      </button>
    </div>
  );
};

export default BrainBankSyncStrip;
